import * as React from 'react'
import { AppService } from '@halo/services/app.service'
import { Dropdown, Form, FormInstance, Input, Menu, Modal, Radio, Table, Tooltip } from 'antd'
import {
    CheckCircleFilled,
    CopyOutlined,
    EditOutlined,
    EyeOutlined,
    FolderOutlined,
    InfoCircleOutlined,
    QuestionCircleOutlined,
    ReadOutlined,
    WarningOutlined,
} from '@ant-design/icons'
import { classnames } from '@halo/_utils/classnames'
import './feeds-data-view.scss'
import { useLoadableDataState } from '@pushly/aqe/lib/hooks/index'
import { TablePaginationConfig } from 'antd/lib/table/interface'
import { convertToStringDay } from '@halo/_utils/relative-language-builder.util'
import { RuleObject } from 'antd/lib/form/index'
import { FeedService } from '@halo/services/feed.service'
import { stripUndefined } from '@halo/helpers/clean-objects'
import { Button } from '@pushly/aqe/lib/components'
import { FeedSendStrategy } from '@pushly/aqe/lib/enums/index'
import moment from 'moment-timezone'
import { secondsToHours, secondsToMinutes } from '@pushly/aqe/lib/utils/index'
import deepEqual from 'react-fast-compare'
import { FdfFormMode } from '@halo/enums/fdf-form-mode.enum'
import { DomainDto } from '@halo/dtos/domain.dto'
import { useService } from '@pushly/aqe/lib/hooks'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { WebBadge } from '@pushly/aqe/lib/components/badges/web-badge'
import { NativeIosBadge } from '@pushly/aqe/lib/components/badges/native-ios-badge'
import { NativeAndroidBadge } from '@pushly/aqe/lib/components/badges/native-android-badge'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { useStickyValue } from '@pushly/aqe/lib/hooks/use-sticky-value'
import { DataView } from '@pushly/aqe/lib/components/data-view/data-view'
import { DataViewContext } from '@pushly/aqe/lib/components/data-view/context'
import { useDataViewReducer } from '@pushly/aqe/lib/components/data-view/reducer'
import { FeedResponse } from '@pushly/models/lib/structs/feeds/feed-response'
import { DataSourceResponse, DataViewProps } from '@pushly/aqe/lib/components/data-view/types'
import { Feed } from '@pushly/models/lib/structs/feeds/feed'
import titleCase from 'title-case'
import { FeedAudienceBadge } from '@pushly/aqe/lib/components/feed-audience-badge/feed-audience-badge'

interface IProps {
    domainId: number
    domain: DomainDto
}

enum FeedStatus {
    ENABLED = 'enabled',
    DISABLED = 'disabled',
}

const STATUS_OPTIONS = [
    { value: FeedStatus.ENABLED, label: titleCase(FeedStatus.ENABLED) },
    { value: FeedStatus.DISABLED, label: titleCase(FeedStatus.DISABLED) },
]

export const FeedsDataView = (props: IProps) => {
    const appSvc = useService(AppService)
    const feedSvc = useService(FeedService)
    const formRef = React.createRef<FormInstance>()

    const [context, dispatch, requestDataSourceReload] = useDataViewReducer<FeedResponse>({
        id: `feeds`,
        defaultPageSize: 20,
    })

    // ---------------------------------- Stateful Variables ---------------------------------- //
    const [pagination, setPagination] = React.useState<TablePaginationConfig>({
        pageSize: 20,
        total: 20,
        current: 1,
        showTotal: (total: number, range: number[]) => `Showing ${range[0]}-${range[1]} of ${total} Feeds`,
        size: 'small',
        showSizeChanger: false,
        onChange: async (currPage: number) => {
            await setPagination((config) => ({ ...config, current: currPage }))
        },
        itemRender: (current: any, type: 'prev' | 'next' | 'page', originalElement: any) => {
            const elementMap = {
                prev: <a>Previous</a>,
                next: <a>Next</a>,
                page: '',
            }

            return elementMap[type] ? elementMap[type] : originalElement
        },
    })
    const [query, setQuery] = React.useState<string>()

    const defaultStatusFilterValue = [FeedStatus.ENABLED, FeedStatus.DISABLED]
    const [getStatusFilter, setStatusFilter] = useStickyValue(
        localStorage,
        `sw.feeds`,
        `d.${props.domainId}.shared.f.status`,
        defaultStatusFilterValue,
    )
    const statusFilter = getStatusFilter()

    const fetchFeeds: DataViewProps<Feed>['dataSourceLoader'] = async (filters, tableState) => {
        if (filters.status && !deepEqual(filters.status, statusFilter)) {
            setStatusFilter(filters.status)
        }

        const apiOptions = stripUndefined({
            query: {
                search: filters.search ?? undefined,
                page: tableState.pagination.current,
                limit: tableState.pagination.pageSize,
                status: filters.status?.length
                    ? filters.status.join(',')
                    : statusFilter.length
                    ? statusFilter.join(',')
                    : undefined,
            },
        })

        const res = await feedSvc.fetchDomainFeeds(props.domainId, apiOptions)
        let dataViewResponse: DataSourceResponse = {
            meta: {},
            cancelled: false,
            ...res,
            data: [],
        }

        if (!res.cancelled) {
            if (res.ok && res.data) {
                dataViewResponse.data = res.data
            } else if (res.error) {
                console.warn(res.error)
            }
        }

        return dataViewResponse
    }

    // ---------------------------------- Action Functions ---------------------------------- //
    const renderActions = React.useCallback(
        (feed: FeedResponse) => {
            const actions: any[] = []

            actions.push({
                key: 'edit',
                text: 'Edit',
                icon: <EditOutlined />,
            })

            actions.push({
                key: 'duplicate',
                text: 'Duplicate',
                icon: <CopyOutlined />,
            })

            actions.push({
                key: 'archive',
                text: 'Archive',
                icon: <FolderOutlined />,
            })

            if (feed.enabled) {
                actions.push({
                    key: 'disable',
                    text: 'Disable',
                    icon: <EyeOutlined />,
                })
            } else {
                actions.push({
                    key: 'enable',
                    text: 'Enable',
                    icon: <EyeOutlined />,
                })
            }

            return (
                <Dropdown.Button
                    size="small"
                    className="feeds-row-actions-btn"
                    onClick={() => handlePrimaryClick(feed)}
                    overlay={
                        actions.length === 0 ? undefined : (
                            <Menu onClick={({ key, domEvent }) => handleRowAction(key, feed, props.domain, domEvent)}>
                                {actions.map((item) => (
                                    <Menu.Item key={`${item.key}`}>
                                        {item.icon}
                                        <span>{item.text}</span>
                                    </Menu.Item>
                                ))}
                            </Menu>
                        )
                    }
                >
                    <span>View</span>
                </Dropdown.Button>
            )
        },
        [props.domain.id],
    )

    function handleRowAction(
        key: string | number,
        feed: FeedResponse,
        domain: DomainDto,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void {
        if (key === 'edit') {
            handleEditFeedClick(feed)
        } else if (key === 'duplicate') {
            handleDuplicateClick(feed)
        } else if (key === 'archive') {
            Modal.confirm({
                title: 'Archive Feed',
                icon: <WarningOutlined />,
                okText: 'Archive',
                onOk: async () => {
                    const form = formRef.current

                    if (!!form) {
                        await form.validateFields()
                        return handleArchiveFeed(feed)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>
                            Are you sure you want to archive this feed? The feed configuration will no longer be
                            available to enable.
                        </p>

                        <p>
                            Please type the Feed name, <b>{feed.name}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule: RuleObject, value, callback) => {
                                        if (!value?.trim()) {
                                            callback('Feed name is required.')
                                        } else if (value !== feed.name) {
                                            callback('Feed name does not match.')
                                        } else {
                                            callback()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'disable') {
            Modal.confirm({
                title: 'Disable Feed',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    const form = formRef.current

                    if (form) {
                        await form.validateFields()
                        return handleFeedEnabledStatus(feed, false)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>Are you sure you want to disable this feed?</p>

                        <p>
                            Please type the Feed name, <b>{feed.name}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule: RuleObject, value, callback) => {
                                        if (!value?.trim()) {
                                            callback('Feed name is required.')
                                        } else if (value !== feed.name) {
                                            callback('Feed name does not match.')
                                        } else {
                                            callback()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else {
            Modal.confirm({
                title: 'Enable Feed',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => handleFeedEnabledStatus(feed, true),
                content: <p>Are you sure you want to enable this feed?</p>,
            } as any)
        }
    }

    const handlePrimaryClick = React.useCallback(
        (feed: FeedResponse) => {
            const url = `/domains/${props.domain.id}/feeds/${feed.id}`
            return appSvc.route(url, { feed, domain: props.domain })
        },
        [props.domain.id],
    )

    const handleDuplicateClick = React.useCallback(
        (feed: FeedResponse) => {
            const url = `/domains/${props.domainId}/feeds/new`

            const { id, ...feedWithoutId } = feed
            const duplicateFeed = {
                ...feedWithoutId,
                name: '(copy) - ' + feed.name,
            }

            return appSvc.route(url, { feed: duplicateFeed, mode: FdfFormMode.DUPLICATE, domain: props.domain })
        },
        [props.domain.id],
    )

    const handleEditFeedClick = React.useCallback(
        (feed: FeedResponse) => {
            const url = `/domains/${props.domain.id}/feeds/${feed.id}/edit`
            return appSvc.route(url, { feed, mode: FdfFormMode.EDIT, domain: props.domain })
        },
        [props.domain.id],
    )

    const handleNewFeedClick = React.useCallback(() => {
        appSvc.route(`/domains/${props.domainId}/feeds/new`, { mode: FdfFormMode.CREATE, domain: props.domain })
    }, [props.domain.id])

    // ---------------------------------- Service Calls ---------------------------------- //
    const handleFeedEnabledStatus = async (feed: FeedResponse, enabled: boolean) => {
        await feedSvc.patchFeed(props.domainId, feed.id, {
            enabled,
        })

        requestDataSourceReload()
    }

    const handleArchiveFeed = async (feed: FeedResponse) => {
        await feedSvc.archiveFeed(props.domainId, feed.id)

        requestDataSourceReload()
    }

    const searchFeeds = async (value: string): Promise<void> => {
        let qString = value.trim()

        if (qString.length < 2) {
            qString = undefined
        }

        if (!deepEqual(query, value)) {
            setQuery(qString)
        }
    }

    const filterFeeds = async (nextStatusFilter: string[]) => {
        if (!deepEqual(nextStatusFilter, statusFilter)) {
            setStatusFilter(nextStatusFilter)
        }
    }

    return (
        <div className={classnames('domain-feeds-list')}>
            <DataViewContext.Provider value={{ state: context, dispatch }}>
                <DataView
                    hideRefresh={true}
                    hideAutoRefreshOptions={true}
                    dataSourceLoader={fetchFeeds}
                    actions={
                        <Button type="primary" size="small" onClick={handleNewFeedClick}>
                            Create Feed
                        </Button>
                    }
                    filters={[
                        {
                            key: 'search',
                            type: 'search',
                            componentProps: {
                                className: classnames('feed-list-search'),
                                placeholder: 'Search Feed ID or Name',
                            },
                        },
                        {
                            key: 'status',
                            type: 'select',
                            componentProps: {
                                className: 'status-filter-select',
                                prefix: 'Status',
                                placeholder: 'Select Statuses',
                                options: STATUS_OPTIONS,
                                value: statusFilter,
                                disableSearch: true,
                                disableSelectAll: true,
                                closeOnEscape: true,
                                maxDisplayCount: 2,
                                maxDisplayFormatter: (options) => {
                                    const multi = options.length > 1
                                    return (
                                        <Tooltip title={options.map((o) => o.label).join(', ')}>
                                            {options.length} {multi ? 'Statuses' : 'Status'}
                                        </Tooltip>
                                    )
                                },
                            },
                        },
                    ]}
                    table={{
                        rowKey: (row) => row.id!,
                        noData: {
                            text: 'No Feeds',
                            icon: <ReadOutlined />,
                        },
                        rowClassName: 'data-view-row-collapsed',
                    }}
                >
                    <Table.Column align="center" className="id" dataIndex="id" key="id" title="ID" />
                    <Table.Column
                        align="center"
                        className="enabled"
                        dataIndex="enabled"
                        key="enabled"
                        title="Enabled"
                        render={(enabled: boolean) => {
                            return enabled ? (
                                <CheckCircleFilled />
                            ) : (
                                <Radio style={{ pointerEvents: 'none' }} checked={false} />
                            )
                        }}
                    />
                    <Table.Column align="left" className="name" dataIndex="name" key="name" title="Feed Name" />
                    <Table.Column
                        align="center"
                        className="channels"
                        dataIndex=""
                        key="channels"
                        title="Channels"
                        render={(data) => {
                            return (
                                <div className="channel-badges">
                                    {data?.config?.channels?.includes(DeliveryChannel.WEB) && <WebBadge />}
                                    {data?.config?.channels?.includes(DeliveryChannel.NATIVE_IOS) && <NativeIosBadge />}
                                    {data?.config?.channels?.includes(DeliveryChannel.NATIVE_ANDROID) && (
                                        <NativeAndroidBadge />
                                    )}
                                </div>
                            )
                        }}
                    />
                    <Table.Column
                        align="left"
                        className="audiences"
                        dataIndex=""
                        key="audiences"
                        title="Audience"
                        render={(data) => {
                            return (
                                <div className="contained">
                                    {<FeedAudienceBadge source={data} appService={appSvc} hideTooltip={false} />}
                                </div>
                            )
                        }}
                    />
                    <Table.Column
                        align="center"
                        className="delivery-strategy"
                        dataIndex=""
                        key="delivery-strategy"
                        title={
                            <div className="delivery-strategy-label">
                                Delivery Strategy
                                <Tooltip
                                    overlayClassName="wide-tooltip"
                                    title={
                                        <React.Fragment>
                                            <p>
                                                The delivery strategy determines when and how often a notification will
                                                be created for a feed:
                                            </p>

                                            <p>
                                                &#8226; ASAP: A notification will be created every time a new article is
                                                discovered and a previous notification has not been sent based on the
                                                specified send frequency.
                                            </p>

                                            <p>
                                                &#8226; Fixed: A notification will be scheduled once a day at the
                                                specified send time in the domain’s time zone. If a new article is not
                                                found at the specified time we will continue checking for a maximum of 1
                                                hour after the send time.
                                            </p>

                                            <p>
                                                &#8226; Subscriber Time Zone: A notification will be scheduled at the
                                                specified send time in each subscriber’s time zone. In order to account
                                                for all time zones the article will be chosen at least 24 hours in
                                                advance of the send time.
                                            </p>
                                        </React.Fragment>
                                    }
                                >
                                    <InfoCircleOutlined />
                                </Tooltip>
                            </div>
                        }
                        render={(data) => {
                            const schedule = data.config?.schedule
                            return (
                                <div className="delivery-strategy-data-view">
                                    <div className="delivery-strategy-display">
                                        <span>{`${
                                            (schedule?.sendStrategy === FeedSendStrategy.FIXED && 'Fixed') ||
                                            (schedule?.sendStrategy === FeedSendStrategy.ASAP && 'ASAP') ||
                                            (schedule?.sendStrategy === FeedSendStrategy.SUBSCRIBER_TIME_ZONE &&
                                                'STZ') ||
                                            ''
                                        }`}</span>
                                    </div>
                                </div>
                            )
                        }}
                    />
                    <Table.Column
                        align="left"
                        className="send-time"
                        dataIndex=""
                        key="send-time"
                        title="Send Time"
                        render={(data) => {
                            const schedule = data.config?.schedule
                            return schedule?.startTime ? (
                                <div className="send-time-data-view">
                                    <div className="send-time-display">
                                        <span>
                                            {(schedule?.sendStrategy === FeedSendStrategy.ASAP &&
                                                `Every ${secondsToMinutes(
                                                    schedule?.minFrequency?.intervalSeconds,
                                                )} Minutes on `) ||
                                                (schedule?.sendStrategy === FeedSendStrategy.FIXED &&
                                                    `${moment(schedule?.startTime, 'HH:mm')?.format(
                                                        'h:mm A',
                                                    )} ${moment()
                                                        .tz(props.domain?.timezone)
                                                        ?.format('z')} on `) ||
                                                (schedule?.sendStrategy === FeedSendStrategy.SUBSCRIBER_TIME_ZONE &&
                                                    `${moment(schedule?.startTime, 'HH:mm')?.format(
                                                        'h:mm A',
                                                    )} STZ on `)}
                                        </span>
                                        <span className="send-time-text">{`${convertToStringDay(
                                            schedule.dayOfWeekIso,
                                        )}`}</span>
                                        <span>
                                            {schedule?.sendStrategy === FeedSendStrategy.ASAP &&
                                                ` (${moment(schedule?.startTime, 'HH:mm').format('h:mm A')} - ${moment(
                                                    schedule?.endTime,
                                                    'HH:mm',
                                                ).format('h:mm A')})`}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <span>New Article Posted</span>
                            )
                        }}
                    />
                    <Table.Column
                        align="center"
                        className="delivery-delay"
                        colSpan={1}
                        dataIndex=""
                        key="delivery-delay"
                        title="Pre-Schedule"
                        render={(data) => {
                            const deliveryDelay = data.config?.schedule?.deliveryDelay
                            return deliveryDelay ? (
                                <span className="contained">
                                    {deliveryDelay.displayMetric === RelativeDateDisplayMetric.MINUTES
                                        ? `${secondsToMinutes(deliveryDelay.intervalSeconds)} ${
                                              deliveryDelay.displayMetric
                                          }`
                                        : `${secondsToHours(deliveryDelay.intervalSeconds)} ${
                                              deliveryDelay.displayMetric
                                          }`}
                                </span>
                            ) : (
                                <span>None</span>
                            )
                        }}
                    />
                    <Table.Column
                        align="center"
                        className="actions"
                        dataIndex=""
                        key="actions"
                        title="Actions"
                        render={(data) => renderActions(data)}
                    />
                </DataView>
            </DataViewContext.Provider>
        </div>
    )
}
